import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Activity, Clock, ChevronRight, ChevronDown, Search, Filter,
  CheckCircle, XCircle, AlertTriangle, Zap, BarChart3, ArrowLeft,
  Loader, ExternalLink, Play, Wrench, Brain, Pause
} from 'lucide-react'
import type { Trace, TraceStep } from '../engine/types'
import { listTraces, getTrace } from '../engine/TraceRecorder'
import { useLang } from '../i18n/LangContext'

const stepColors: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  tool_call: { bg: 'rgba(108,140,255,0.12)', text: '#6c8cff', icon: <Wrench size={14} /> },
  thinking: { bg: 'rgba(160,120,255,0.12)', text: '#a078ff', icon: <Brain size={14} /> },
  finish: { bg: 'rgba(74,222,128,0.12)', text: '#4ade80', icon: <CheckCircle size={14} /> },
  error: { bg: 'rgba(248,113,113,0.12)', text: '#f87171', icon: <XCircle size={14} /> },
}

const statusColors: Record<string, string> = {
  completed: '#4ade80',
  running: '#fbbf24',
  error: '#f87171',
  timeout: '#f87171',
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  return `${m}m ${s}s`
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('zh-CN', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function TraceViewerPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useLang()
  const [traces, setTraces] = useState<Trace[]>([])
  const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (id) {
      loadTraceDetail(id)
    } else {
      loadTraceList()
    }
  }, [id])

  async function loadTraceList() {
    setLoading(true)
    setError(null)
    try {
      const data = await listTraces()
      setTraces(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load traces')
    } finally {
      setLoading(false)
    }
  }

  async function loadTraceDetail(traceId: string) {
    setLoading(true)
    setError(null)
    try {
      const data = await getTrace(traceId)
      setSelectedTrace(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load trace')
    } finally {
      setLoading(false)
    }
  }

  function toggleStepExpand(stepNum: number) {
    setExpandedSteps(prev => {
      const next = new Set(prev)
      if (next.has(stepNum)) next.delete(stepNum)
      else next.add(stepNum)
      return next
    })
  }

  if (loading) {
    return (
      <div className="container-page px-4 py-12">
        <div className="flex items-center justify-center py-24">
          <Loader className="animate-spin" size={32} style={{ color: 'var(--color-accent)' }} />
          <span className="ml-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container-page px-4 py-12">
        <div className="flex flex-col items-center justify-center py-24">
          <AlertTriangle size={48} style={{ color: 'var(--color-danger)' }} />
          <p className="mt-4 text-lg" style={{ color: 'var(--color-danger)' }}>{error}</p>
          <button
            onClick={() => id ? loadTraceDetail(id) : loadTraceList()}
            className="mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  // Trace detail view
  if (selectedTrace) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => { setSelectedTrace(null); navigate('/traces') }}
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <ArrowLeft size={18} style={{ color: 'var(--color-text-secondary)' }} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Trace: {selectedTrace.trace_id}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Agent: {selectedTrace.agent_name} · Task: {selectedTrace.task_id} · {selectedTrace.steps_count} steps · {formatDuration(selectedTrace.duration_ms)}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span
              className="px-2.5 py-1 rounded-md text-xs font-medium"
              style={{ color: statusColors[selectedTrace.status] || '#8b8fa8', backgroundColor: `${statusColors[selectedTrace.status] || '#8b8fa8'}20` }}
            >
              {selectedTrace.status.toUpperCase()}
            </span>
            <button
              onClick={() => navigate(`/reports/${selectedTrace.trace_id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors"
              style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
            >
              <BarChart3 size={14} />
              {t('trace.viewReport')}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Steps', value: selectedTrace.steps_count },
            { label: 'Duration', value: formatDuration(selectedTrace.duration_ms) },
            { label: 'Status', value: selectedTrace.status },
            { label: 'Agent', value: selectedTrace.agent_name },
          ].map((card, i) => (
            <div key={i} className="p-4 rounded-xl" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{card.label}</p>
              <p className="text-lg font-semibold mt-1" style={{ color: 'var(--color-text-primary)' }}>{card.value}</p>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>{t('trace.timeline')}</h2>
          <div className="relative">
            {selectedTrace.steps && selectedTrace.steps.length > 0 ? (
              <div className="relative pl-8">
                <div className="absolute left-3 top-2 bottom-2 w-0.5" style={{ backgroundColor: 'var(--color-border)' }} />
                {selectedTrace.steps.map((step: TraceStep) => {
                  const colors = stepColors[step.action.type] || stepColors.tool_call
                  const isExpanded = expandedSteps.has(step.step_number)

                  return (
                    <div key={step.step_number} className="relative mb-2">
                      <div
                        className="absolute left-[-1.35rem] top-2 w-3 h-3 rounded-full border-2"
                        style={{ backgroundColor: colors.bg, borderColor: colors.text }}
                      />
                      <div
                        className="p-3 rounded-lg cursor-pointer transition-colors"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                        onClick={() => toggleStepExpand(step.step_number)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={14} style={{ color: 'var(--color-text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--color-text-muted)' }} />}
                          <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>#{step.step_number}</span>
                          <span className="px-1.5 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: colors.bg, color: colors.text }}>
                            {step.action.type.replace('_', ' ')}
                          </span>
                          {step.action.tool && (
                            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{step.action.tool}</span>
                          )}
                          <span className="text-xs ml-auto" style={{ color: 'var(--color-text-muted)' }}>{new Date(step.timestamp).toLocaleTimeString('zh-CN')}</span>
                          {step.observation && (
                            <span style={{ color: step.observation.status === 'success' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                              {step.observation.status === 'success' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                            </span>
                          )}
                        </div>

                        {isExpanded && (
                          <div className="mt-3 pl-4 space-y-2 text-xs" style={{ borderLeft: '2px solid var(--color-border)' }}>
                            {step.action.args && (
                              <div>
                                <span className="font-medium" style={{ color: 'var(--color-text-muted)' }}>Args:</span>
                                <pre className="mt-1 p-2 rounded overflow-x-auto" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                                  {JSON.stringify(step.action.args, null, 2)}
                                </pre>
                              </div>
                            )}
                            {step.action.reasoning && (
                              <div>
                                <span className="font-medium" style={{ color: 'var(--color-text-muted)' }}>Reasoning:</span>
                                <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>{step.action.reasoning}</p>
                              </div>
                            )}
                            {step.observation?.data && (
                              <div>
                                <span className="font-medium" style={{ color: 'var(--color-text-muted)' }}>Observation:</span>
                                <pre className="mt-1 p-2 rounded overflow-x-auto max-h-40" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                                  {JSON.stringify(step.observation.data, null, 2)}
                                </pre>
                              </div>
                            )}
                            {step.observation?.error && (
                              <div className="p-2 rounded" style={{ backgroundColor: 'rgba(248,113,113,0.1)', color: 'var(--color-danger)' }}>
                                <span className="font-medium">{step.observation.error.code}:</span> {step.observation.error.message}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Pause size={36} style={{ color: 'var(--color-text-muted)' }} />
                <p className="mt-3" style={{ color: 'var(--color-text-muted)' }}>{t('trace.noSteps')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Trace list view
  const filteredTraces = traces.filter(t =>
    !searchQuery ||
    t.trace_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.agent_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.task_id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('trace.title')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{t('trace.subtitle')}</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
        <input
          type="text"
          placeholder={t('trace.search')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
        />
      </div>

      {/* Trace List */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
        {filteredTraces.length === 0 ? (
          <div className="text-center py-16">
            <Activity size={40} style={{ color: 'var(--color-text-muted)' }} />
            <p className="mt-3 text-lg" style={{ color: 'var(--color-text-muted)' }}>{t('trace.noTraces')}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>{t('trace.runToGenerate')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Trace ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Agent</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Task</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Steps</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Created</th>
                  <th className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredTraces.map(trace => (
                  <tr
                    key={trace.trace_id}
                    className="cursor-pointer transition-colors hover:opacity-80"
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                    onClick={() => navigate(`/traces/${trace.trace_id}`)}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono" style={{ color: 'var(--color-accent)' }}>{trace.trace_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{trace.agent_name}</span>
                      <span className="text-xs ml-2 px-1.5 py-0.5 rounded" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)' }}>{trace.agent_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>{trace.task_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ color: statusColors[trace.status] || '#8b8fa8', backgroundColor: `${statusColors[trace.status] || '#8b8fa8'}20` }}>
                        {trace.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{trace.steps_count}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{formatDuration(trace.duration_ms)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{formatTime(trace.created_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight size={16} style={{ color: 'var(--color-text-muted)' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}